'use client';

import { useState, useEffect, useRef, Suspense } from 'react';
import { useSearchParams } from 'next/navigation';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import Link from 'next/link';
import Image from 'next/image';
import { Turnstile, type TurnstileInstance } from '@marsidev/react-turnstile';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Alert, AlertDescription } from '../components/ui/alert';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Footer } from '../components/Footer';

const COUNTRIES = [
  { value: 'US', label: 'United States' },
  { value: 'CA', label: 'Canada' },
  { value: 'GB', label: 'United Kingdom' },
  { value: 'AU', label: 'Australia' },
  { value: 'NZ', label: 'New Zealand' },
  { value: 'IE', label: 'Ireland' },
  { value: 'DE', label: 'Germany' },
  { value: 'FR', label: 'France' },
  { value: 'NL', label: 'Netherlands' },
  { value: 'ZA', label: 'South Africa' },
  { value: 'KE', label: 'Kenya' },
  { value: 'NG', label: 'Nigeria' },
  { value: 'GH', label: 'Ghana' },
  { value: 'SG', label: 'Singapore' },
  { value: 'PH', label: 'Philippines' },
  { value: 'KR', label: 'South Korea' },
  { value: 'JP', label: 'Japan' },
  { value: 'BR', label: 'Brazil' },
  { value: 'MX', label: 'Mexico' },
  { value: 'OTHER', label: 'Other' },
];

const FOCUS_AREAS = [
  { value: 'leadership', label: 'Leadership Development' },
  { value: 'strategy', label: 'Strategic Planning' },
  { value: 'team', label: 'Team Building' },
  { value: 'growth', label: 'Organizational Growth' },
  { value: 'culture', label: 'Culture Transformation' },
  { value: 'succession', label: 'Succession Planning' },
  { value: 'coaching', label: 'Executive Coaching' },
  { value: 'other', label: 'Other' },
];

const US_STATES = [
  { value: 'AL', label: 'Alabama' },
  { value: 'AK', label: 'Alaska' },
  { value: 'AZ', label: 'Arizona' },
  { value: 'AR', label: 'Arkansas' },
  { value: 'CA', label: 'California' },
  { value: 'CO', label: 'Colorado' },
  { value: 'CT', label: 'Connecticut' },
  { value: 'DE', label: 'Delaware' },
  { value: 'FL', label: 'Florida' },
  { value: 'GA', label: 'Georgia' },
  { value: 'HI', label: 'Hawaii' },
  { value: 'ID', label: 'Idaho' },
  { value: 'IL', label: 'Illinois' },
  { value: 'IN', label: 'Indiana' },
  { value: 'IA', label: 'Iowa' },
  { value: 'KS', label: 'Kansas' },
  { value: 'KY', label: 'Kentucky' },
  { value: 'LA', label: 'Louisiana' },
  { value: 'ME', label: 'Maine' },
  { value: 'MD', label: 'Maryland' },
  { value: 'MA', label: 'Massachusetts' },
  { value: 'MI', label: 'Michigan' },
  { value: 'MN', label: 'Minnesota' },
  { value: 'MS', label: 'Mississippi' },
  { value: 'MO', label: 'Missouri' },
  { value: 'MT', label: 'Montana' },
  { value: 'NE', label: 'Nebraska' },
  { value: 'NV', label: 'Nevada' },
  { value: 'NH', label: 'New Hampshire' },
  { value: 'NJ', label: 'New Jersey' },
  { value: 'NM', label: 'New Mexico' },
  { value: 'NY', label: 'New York' },
  { value: 'NC', label: 'North Carolina' },
  { value: 'ND', label: 'North Dakota' },
  { value: 'OH', label: 'Ohio' },
  { value: 'OK', label: 'Oklahoma' },
  { value: 'OR', label: 'Oregon' },
  { value: 'PA', label: 'Pennsylvania' },
  { value: 'RI', label: 'Rhode Island' },
  { value: 'SC', label: 'South Carolina' },
  { value: 'SD', label: 'South Dakota' },
  { value: 'TN', label: 'Tennessee' },
  { value: 'TX', label: 'Texas' },
  { value: 'UT', label: 'Utah' },
  { value: 'VT', label: 'Vermont' },
  { value: 'VA', label: 'Virginia' },
  { value: 'WA', label: 'Washington' },
  { value: 'WV', label: 'West Virginia' },
  { value: 'WI', label: 'Wisconsin' },
  { value: 'WY', label: 'Wyoming' },
  { value: 'DC', label: 'District of Columbia' },
];

const formSchema = z.object({
  name: z.string().min(2, 'Name must be at least 2 characters').max(100),
  email: z.string().email('Please enter a valid email address'),
  phone: z.string().max(30).optional(),
  organizationName: z.string().max(200).optional(),
  country: z.string().optional(),
  state: z.string().optional(),
  message: z.string().min(10, 'Message must be at least 10 characters').max(1000),
  focusArea: z.string().max(200).optional(),
  preferredStartDate: z.string().max(50).optional(),
  website: z.string().max(0).optional(),
});

type FormData = z.infer<typeof formSchema>;

const TEST_DATA_CONSULTING = {
  name: 'John Test Smith',
  email: 'john.test@example.com',
  phone: '+1 555-123-4567',
  organizationName: 'Test Church International',
  country: 'US',
  state: 'TX',
  focusArea: 'leadership',
  preferredStartDate: '2025-03-15',
  message: 'This is a test message for the consulting form. We are looking to improve our leadership development program and would love to discuss potential partnership opportunities.',
};

function ConsultingPageContent() {
  const searchParams = useSearchParams();
  const isTestForm = searchParams.get('testform') === 'true';

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<{
    type: 'success' | 'error';
    message: string;
  } | null>(null);
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const turnstileRef = useRef<TurnstileInstance>(null);

  // Skip Turnstile on localhost
  const isLocalhost = typeof window !== 'undefined' &&
    (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
    watch,
  } = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: isTestForm ? TEST_DATA_CONSULTING : undefined,
  });

  // Reset form with test data after mount to handle hydration timing
  useEffect(() => {
    if (isTestForm) {
      reset(TEST_DATA_CONSULTING);
    }
  }, [isTestForm, reset]);

  const onSubmit = async (data: FormData) => {
    // Skip Turnstile check on localhost
    if (!isLocalhost && !turnstileToken) {
      setSubmitStatus({
        type: 'error',
        message: 'Please complete the security verification.',
      });
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      const response = await fetch('/api/consulting', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...data,
          turnstileToken: isLocalhost ? 'localhost-bypass' : turnstileToken,
        }),
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || 'Failed to submit request');
      }

      setIsSubmitted(true);
      reset();
      setTurnstileToken(null);
      turnstileRef.current?.reset();
    } catch (error: any) {
      setSubmitStatus({
        type: 'error',
        message: error.message || 'Failed to submit request. Please try again later.',
      });
      // Reset turnstile on error so user can try again
      turnstileRef.current?.reset();
      setTurnstileToken(null);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <main className="min-h-screen bg-background">
      {/* Hero with background image */}
      <div className="relative h-[450px] md:h-[550px] overflow-hidden">
        <Image
          src="/consulting-2.jpg"
          alt="Consulting session background"
          fill
          className="object-cover"
          priority
          sizes="100vw"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-primary/60 via-primary/40 to-primary/70" />

        {/* Decorative elements - partnership/connection theme */}
        <div className="absolute top-1/3 left-8 opacity-[0.08] hidden lg:block">
          <div className="relative">
            <div className="w-24 h-24 rounded-full border-2 border-white" />
            <div className="w-24 h-24 rounded-full border-2 border-white absolute top-0 left-14" />
          </div>
        </div>
        <div className="absolute bottom-1/4 right-12 w-20 h-20 border border-accent/20 rounded-full hidden lg:block" />
        <div className="absolute top-1/4 right-24 w-12 h-12 border border-white/10 rounded-full hidden lg:block" />

        {/* Back link */}
        <div className="absolute top-0 left-0 right-0 z-10">
          <div className="max-w-4xl mx-auto px-6 lg:px-8 py-6">
            <Link
              href="/"
              className="group inline-flex items-center gap-2 text-sm text-white/80 hover:text-white transition-colors"
            >
              <svg
                className="w-4 h-4 transition-transform group-hover:-translate-x-1"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              Back to Home
            </Link>
          </div>
        </div>

        {/* Hero content */}
        <div className="absolute inset-0 flex items-center justify-center text-center px-6">
          <div className="max-w-2xl">
            <span className="inline-flex items-center gap-2 text-sm font-medium tracking-wider uppercase text-white/70 mb-4">
              <span className="w-6 h-px bg-accent/70" />
              Consulting
            </span>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight text-white">
              Partner with Andrew
            </h1>
          </div>
        </div>
      </div>

      {/* Form section - overlaps hero */}
      <div className="relative z-10 -mt-20 pb-20 lg:pb-32">
        <div className="max-w-4xl mx-auto px-6 lg:px-8">
          <div className="bg-background border border-border shadow-2xl shadow-primary/5 overflow-hidden">
            {/* Accent bar */}
            <div className="h-1 bg-gradient-to-r from-primary via-primary/80 to-accent/60" />

            <div className="p-8 md:p-12 lg:p-16">
              {isSubmitted ? (
                /* Success Confirmation */
                <div className="text-center py-12 lg:py-20">
                  <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-accent/10 mb-8">
                    <svg className="w-10 h-10 text-accent" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
                    Request Received
                  </h2>
                  <p className="text-lg text-muted-foreground max-w-md mx-auto mb-8">
                    Thank you for reaching out. We will review your inquiry and respond shortly.
                  </p>
                  <Link
                    href="/"
                    className="inline-flex items-center gap-2 text-primary hover:text-primary/80 font-medium transition-colors"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                    </svg>
                    Return to Home
                  </Link>
                </div>
              ) : (
                <>
                  {/* Intro text */}
                  <div className="mb-10 pb-8 border-b border-border/60">
                    <p className="text-lg text-muted-foreground leading-relaxed">
                      Looking for strategic guidance to help your organization thrive?
                      Let&apos;s discuss how we can work together to create lasting impact.
                    </p>
                  </div>

                  <form onSubmit={handleSubmit(onSubmit)} className="space-y-10">
                {/* Honeypot */}
                <input
                  type="text"
                  {...register('website')}
                  tabIndex={-1}
                  autoComplete="off"
                  style={{ display: 'none' }}
                  aria-hidden="true"
                />

                {/* Contact Information Section */}
                <div className="space-y-6">
                  <h2 className="text-sm font-medium tracking-wider uppercase text-primary flex items-center gap-3">
                    <span className="w-8 h-px bg-primary" />
                    Contact Information
                  </h2>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="name" className="text-foreground font-medium">
                        Name <span className="text-accent">*</span>
                      </Label>
                      <Input
                        id="name"
                        type="text"
                        required
                        {...register('name')}
                        autoComplete="name"
                        className={`h-12 ${errors.name ? 'border-destructive focus:ring-destructive/20' : ''}`}
                        placeholder="Your full name"
                      />
                      {errors.name && (
                        <p className="text-sm text-destructive">{errors.name.message}</p>
                      )}
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="email" className="text-foreground font-medium">
                        Email <span className="text-accent">*</span>
                      </Label>
                      <Input
                        id="email"
                        type="email"
                        required
                        {...register('email')}
                        autoComplete="email"
                        className={`h-12 ${errors.email ? 'border-destructive focus:ring-destructive/20' : ''}`}
                        placeholder="your@email.com"
                      />
                      {errors.email && (
                        <p className="text-sm text-destructive">{errors.email.message}</p>
                      )}
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="phone" className="text-foreground font-medium">
                      Phone <span className="text-muted-foreground font-normal">(Optional)</span>
                    </Label>
                    <Input
                      id="phone"
                      type="tel"
                      {...register('phone')}
                      autoComplete="tel"
                      className="h-12"
                      placeholder="+1 555-123-4567"
                    />
                    <p className="text-xs text-muted-foreground">Include country code for international numbers</p>
                  </div>
                </div>

                {/* Organization Details Section */}
                <div className="space-y-6">
                  <h2 className="text-sm font-medium tracking-wider uppercase text-primary flex items-center gap-3">
                    <span className="w-8 h-px bg-primary" />
                    Organization Details
                  </h2>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="organizationName" className="text-foreground font-medium">
                        Organization Name <span className="text-muted-foreground font-normal">(Optional)</span>
                      </Label>
                      <Input
                        id="organizationName"
                        type="text"
                        {...register('organizationName')}
                        autoComplete="organization"
                        className="h-12"
                        placeholder="Church or organization name"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label className="text-foreground font-medium">
                        Country <span className="text-muted-foreground font-normal">(Optional)</span>
                      </Label>
                      <Select key={`country-${isTestForm}`} value={watch('country')} onValueChange={(value) => {
                        setValue('country', value);
                        if (value !== 'US') setValue('state', '');
                      }}>
                        <SelectTrigger className="h-12">
                          <SelectValue placeholder="Select country" />
                        </SelectTrigger>
                        <SelectContent>
                          {COUNTRIES.map((country) => (
                            <SelectItem key={country.value} value={country.value}>
                              {country.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    {watch('country') === 'US' && (
                      <div className="space-y-2">
                        <Label className="text-foreground font-medium">
                          State <span className="text-muted-foreground font-normal">(Optional)</span>
                        </Label>
                        <Select key={`state-${isTestForm}`} value={watch('state')} onValueChange={(value) => setValue('state', value)}>
                          <SelectTrigger className="h-12">
                            <SelectValue placeholder="Select state" />
                          </SelectTrigger>
                          <SelectContent>
                            {US_STATES.map((state) => (
                              <SelectItem key={state.value} value={state.value}>
                                {state.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    )}
                  </div>
                </div>

                {/* Partnership Details Section */}
                <div className="space-y-6">
                  <h2 className="text-sm font-medium tracking-wider uppercase text-primary flex items-center gap-3">
                    <span className="w-8 h-px bg-primary" />
                    Partnership Details
                  </h2>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label className="text-foreground font-medium">
                        Area of Focus <span className="text-muted-foreground font-normal">(Optional)</span>
                      </Label>
                      <Select key={`focusArea-${isTestForm}`} value={watch('focusArea')} onValueChange={(value) => setValue('focusArea', value)}>
                        <SelectTrigger className="h-12">
                          <SelectValue placeholder="Select focus area" />
                        </SelectTrigger>
                        <SelectContent>
                          {FOCUS_AREAS.map((area) => (
                            <SelectItem key={area.value} value={area.value}>
                              {area.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="preferredStartDate" className="text-foreground font-medium">
                        Preferred Start Date <span className="text-muted-foreground font-normal">(Optional)</span>
                      </Label>
                      <Input
                        id="preferredStartDate"
                        type="date"
                        {...register('preferredStartDate')}
                        className="h-12"
                      />
                    </div>
                  </div>
                </div>

                {/* Message Section */}
                <div className="space-y-6">
                  <h2 className="text-sm font-medium tracking-wider uppercase text-primary flex items-center gap-3">
                    <span className="w-8 h-px bg-primary" />
                    Your Goals
                  </h2>

                  <div className="space-y-2">
                    <Label htmlFor="message" className="text-foreground font-medium">
                      Message <span className="text-accent">*</span>
                    </Label>
                    <Textarea
                      id="message"
                      rows={6}
                      required
                      {...register('message')}
                      placeholder="Tell us about your organization, current challenges, and what you're hoping to achieve through consulting..."
                      className={`resize-none ${errors.message ? 'border-destructive focus:ring-destructive/20' : ''}`}
                    />
                    {errors.message && (
                      <p className="text-sm text-destructive">{errors.message.message}</p>
                    )}
                  </div>
                </div>

                {/* Turnstile Widget - skip on localhost */}
                {!isLocalhost && (
                  <div className="flex justify-center">
                    <Turnstile
                      ref={turnstileRef}
                      siteKey={process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY || ''}
                      onSuccess={setTurnstileToken}
                      onError={() => setTurnstileToken(null)}
                      onExpire={() => setTurnstileToken(null)}
                    />
                  </div>
                )}

                {/* Status Messages */}
                {submitStatus && (
                  <Alert
                    variant={submitStatus.type === 'error' ? 'destructive' : 'default'}
                    className={
                      submitStatus.type === 'success'
                        ? 'bg-accent/10 text-foreground border-accent/30'
                        : ''
                    }
                  >
                    <AlertDescription className="font-medium flex items-center gap-2">
                      {submitStatus.type === 'success' && (
                        <svg className="w-5 h-5 text-accent" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                      )}
                      {submitStatus.message}
                    </AlertDescription>
                  </Alert>
                )}

                {/* Submit Button */}
                <Button
                  type="submit"
                  size="lg"
                  disabled={isSubmitting}
                  className="w-full h-14 text-base font-medium bg-primary hover:bg-primary/90 transition-all duration-300 hover:shadow-lg hover:shadow-primary/20"
                >
                  {isSubmitting ? (
                    <span className="flex items-center gap-2">
                      <svg className="w-5 h-5 animate-spin" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                      </svg>
                      Sending...
                    </span>
                  ) : (
                    'Start the Conversation'
                  )}
                </Button>
                  </form>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </main>
  );
}

export default function ConsultingPage() {
  return (
    <Suspense fallback={<div className="min-h-screen bg-background" />}>
      <ConsultingPageContent />
    </Suspense>
  );
}
